import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { formatMoney } from '../money';
import { formatProviderDateTime } from '../format';
import type { RuteroMoney } from '../queries';
import {
  BOOKING_BLOCKED_REASONS_ID,
  blockedReasonsText,
  bookingCtaA11y,
} from '../bookingDecision';
import type { BookingBlockedIssue, BookingFlag } from '../bookingDecision';
import { passengerFieldRefLabels } from '../passengerFieldLabels';
import { StatusFlag } from './StatusFlag';

const FLAG_KEY: Record<BookingFlag['kind'], string> = {
  instantConfirmation: 'travelServices.booking.instantConfirmation',
  onRequestConfirmation: 'travelServices.booking.onRequestConfirmation',
  freeCancellation: 'travelServices.booking.freeCancellationUntil',
};

interface Props {
  /** El precio autoritativo de ESTE momento. */
  price?: RuteroMoney;
  /** De dónde sale ese número (multiplicación, opciones, avisos de cotización). */
  priceDetails?: ReactNode;
  /** El reloj del momento: vigencia de la tarifa antes del hold, del hold después. */
  countdown?: ReactNode;
  flags: BookingFlag[];
  /** Vacío ⇒ el CTA está habilitado. Nunca un botón gris mudo. */
  blocked: BookingBlockedIssue[];
  /** Usa los mismos rótulos de campos que el formulario del vertical. */
  translatePassengerField?: (key: string) => string;
  /** Cómo se encabeza la lista de motivos: depende de qué se está por hacer. */
  blockedPrefixKey: string;
  primary?: {
    label: string;
    loading: boolean;
    /**
     * Qué hace el botón. Si `blocked` no está vacío no se ejecuta: si el
     * bloqueo viajara además como un `disabled` propio, podría quedar un botón
     * apretable con el motivo escrito al lado (o al revés).
     */
    onClick: () => void;
    /** Qué compromete apretarlo (obligación de pago, tarifa retenida). */
    tooltip?: string;
  };
  /** Cancelar, sincronizar: lo que acompaña a la decisión principal. */
  secondaryActions?: ReactNode;
  closeLabel: string;
  onClose: () => void;
  closeDisabled: boolean;
}

/**
 * La barra de decisión: el precio del momento, cuánto le queda de vida, las
 * condiciones que cambian la decisión y EL botón.
 *
 * Vive en las acciones del diálogo, que en MUI quedan FUERA del área
 * scrolleable (`DialogContent`): el cuerpo scrollea, la decisión no se mueve.
 * Es la columna de decisión de las cards, rotada — y por eso el motivo de un
 * CTA bloqueado va acá, pegado al botón, y no en un aviso tres pantallas
 * arriba.
 */
export function BookingDecisionBar({
  price,
  priceDetails,
  countdown,
  flags,
  blocked,
  translatePassengerField,
  blockedPrefixKey,
  primary,
  secondaryActions,
  closeLabel,
  onClose,
  closeDisabled,
}: Props) {
  const { t } = useTranslation();
  // El motivo y los campos que lo explican salen del MISMO issue: un motivo que
  // nombra datos nunca queda sin nombrarlos.
  const blockedText = blockedReasonsText(blocked, t, (fields) =>
    passengerFieldRefLabels(fields, translatePassengerField ?? t));
  // El estado del CTA, su ancla al motivo y el corte del click salen de la
  // MISMA lista que lo bloquea. El atributo `disabled` nunca se usa: sacaría
  // el botón del orden de tabulación y el teclado se toparía con un botón que
  // no existe, sin oír nunca por qué.
  const ctaBlocked = blocked.length > 0;
  const ctaA11y = bookingCtaA11y(blocked);

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        py: 1.5,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      {/* En mobile el importe se lleva el renglón entero y envuelve antes que
          desbordar: a 375 px un total largo no puede empujar la barra fuera de
          la pantalla. En desktop conserva su ancho mínimo de siempre. */}
      <Box
        minWidth={{ xs: 0, sm: 180 }}
        maxWidth={{ xs: '100%', sm: 'none' }}
        flexBasis={{ xs: '100%', sm: 'auto' }}
      >
        <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
          {price ? formatMoney(price) : '—'}
        </Typography>
        {priceDetails}
      </Box>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {countdown}
        {flags.map((flag) => (
          <StatusFlag
            key={flag.kind}
            tone={flag.tone}
            label={t(FLAG_KEY[flag.kind], {
              date: flag.value ? formatProviderDateTime(flag.value) : '',
            })}
          />
        ))}
      </Stack>

      <Box flexGrow={1} />

      {/* El motivo del bloqueo va antes del botón y sale de la MISMA lista que
          lo deshabilita: no puede existir un gris sin explicación. */}
      {ctaBlocked && (
        <Typography
          id={BOOKING_BLOCKED_REASONS_ID}
          variant="caption"
          color="text.secondary"
          sx={{ maxWidth: { xs: '100%', sm: 360 } }}
        >
          {t(blockedPrefixKey, { reasons: blockedText })}
        </Typography>
      )}

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button onClick={onClose} disabled={closeDisabled}>
          {closeLabel}
        </Button>
        {secondaryActions}
        {primary && (
          <Tooltip title={primary.tooltip ?? ''}>
            {/* El span deja que el tooltip siga apareciendo con el botón
                deshabilitado: el motivo también se lee al lado, en texto. */}
            <span>
              <LoadingButton
                variant="contained"
                loading={primary.loading}
                {...ctaA11y}
                // Bloqueado se ve gris y no hace nada, pero se enfoca y se
                // anuncia: el corte del click sale de la misma expresión que
                // lo pinta, así que no puede quedar uno sin el otro.
                onClick={() => {
                  if (!ctaBlocked) primary.onClick();
                }}
                sx={
                  ctaBlocked
                    ? {
                      bgcolor: 'action.disabledBackground',
                      color: 'action.disabled',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: 'action.disabledBackground',
                        boxShadow: 'none',
                      },
                    }
                    : undefined
                }
              >
                {primary.label}
              </LoadingButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}
